import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { apiClient, API_ENDPOINTS } from '../config/api';

interface ConversationRow {
  id: number;
  username?: string | null;
  subjectLabel: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  lastMessageAt?: string | null;
  lastMessagePreview?: string | null;
  messageCount?: number;
  latestAssistantFeedback?: string | null;
}

interface ConversationDetail {
  id: number;
  username?: string | null;
  subjectLabel: string;
  subjectFreeText?: string | null;
  status: string;
  createdAt: string;
  updatedAt: string;
  lastMessageAt?: string | null;
}

interface ContactMessage {
  id: number;
  role: 'user' | 'assistant';
  username?: string | null;
  messageText: string;
  faqDocumentName?: string | null;
  modelName?: string | null;
  feedbackValue?: 'up' | 'unclear' | null;
  feedbackAt?: string | null;
  createdAt: string;
}

interface ContactHistoryDashboardProps {
  onBackToDeveloper: () => void;
}

const extractErrorMessage = (err: unknown, fallback: string) => {
  if (axios.isAxiosError(err)) {
    return err.response?.data?.error?.message || fallback;
  }
  return err instanceof Error ? err.message : fallback;
};

const parseAsJstNaive = (value: string) => {
  const match = value.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,6}))?)?$/
  );
  if (!match) {
    return null;
  }

  const [, year, month, day, hour, minute, second = '0', fraction = '0'] = match;
  const milliseconds = Number((fraction + '000').slice(0, 3));
  return new Date(
    Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour) - 9,
      Number(minute),
      Number(second),
      milliseconds
    )
  );
};

const parseDateTime = (value?: string | null, assumeJstNaive = false) => {
  if (!value) {
    return null;
  }
  if (assumeJstNaive && !/z$|[+-]\d{2}:\d{2}$/i.test(value)) {
    return parseAsJstNaive(value);
  }
  const normalized = /z$|[+-]\d{2}:\d{2}$/i.test(value) ? value : `${value}Z`;
  return new Date(normalized);
};

const formatDateTime = (value?: string | null, assumeJstNaive = false) => {
  const parsed = parseDateTime(value, assumeJstNaive);
  if (!parsed || Number.isNaN(parsed.getTime())) {
    return '未記録';
  }
  return parsed.toLocaleString('ja-JP', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Tokyo',
  });
};

const shouldTreatCreatedAtAsJstNaive = (createdAt?: string | null, updatedAt?: string | null) => {
  const createdAsUtc = parseDateTime(createdAt);
  const updatedAsUtc = parseDateTime(updatedAt);
  const createdAsJst = parseDateTime(createdAt, true);

  if (!createdAsUtc || !updatedAsUtc || !createdAsJst) {
    return false;
  }
  if (Number.isNaN(createdAsUtc.getTime()) || Number.isNaN(updatedAsUtc.getTime()) || Number.isNaN(createdAsJst.getTime())) {
    return false;
  }

  const utcGap = createdAsUtc.getTime() - updatedAsUtc.getTime();
  const jstGap = createdAsJst.getTime() - updatedAsUtc.getTime();
  return utcGap > 8 * 60 * 60 * 1000 && utcGap < 10 * 60 * 60 * 1000 && Math.abs(jstGap) < 5 * 60 * 1000;
};

const buildCsvDownloadName = () => {
  const parts = new Intl.DateTimeFormat('ja-JP', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date());

  const values = Object.fromEntries(
    parts
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value])
  );

  return `contact-conversations-${values.year}${values.month}${values.day}-${values.hour}${values.minute}${values.second}.csv`;
};

const ContactHistoryDashboard: React.FC<ContactHistoryDashboardProps> = ({ onBackToDeveloper }) => {
  const [conversations, setConversations] = useState<ConversationRow[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<ConversationDetail | null>(null);
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadConversations = useCallback(async () => {
    try {
      const response = await apiClient.get(API_ENDPOINTS.adminContactConversations);
      setConversations(response.data.conversations || []);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'お問い合わせ履歴一覧の取得に失敗しました。'));
    }
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const loadConversationDetail = async (conversationId: number) => {
    setIsLoadingDetail(true);
    try {
      const response = await apiClient.get(API_ENDPOINTS.adminContactConversationDetail(conversationId));
      setSelectedConversation(response.data.conversation || null);
      setMessages(response.data.messages || []);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'お問い合わせ詳細の取得に失敗しました。'));
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const handleDownloadCsv = async () => {
    setIsDownloading(true);
    try {
      const response = await apiClient.get(API_ENDPOINTS.adminContactConversationsCsv, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = buildCsvDownloadName();
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(extractErrorMessage(err, 'CSVダウンロードに失敗しました。'));
    } finally {
      setIsDownloading(false);
    }
  };

  const usesLegacyJstTimestamps = shouldTreatCreatedAtAsJstNaive(
    selectedConversation?.createdAt,
    selectedConversation?.updatedAt
  );

  return (
    <div className="login-activity-page">
      <section className="login-activity-hero">
        <div>
          <span className="login-activity-eyebrow">Admin Visibility</span>
          <h2>お問い合わせ履歴</h2>
          <p>ユーザーからのお問い合わせとAI応答、評価結果を確認できます。</p>
        </div>
        <div className="login-activity-hero-actions">
          <button className="login-activity-back-button" onClick={onBackToDeveloper}>
            管理者画面へ戻る
          </button>
          <button className="login-activity-download" onClick={handleDownloadCsv} disabled={isDownloading}>
            {isDownloading ? 'CSV出力中...' : 'CSVダウンロード'}
          </button>
        </div>
      </section>

      {error && <div className="error-message"><span>{error}</span></div>}

      <section className="login-activity-panel">
        <div className="login-activity-panel-header">
          <div>
            <h3>お問い合わせ一覧</h3>
          </div>
        </div>
        <div className="login-activity-table-wrap">
          <table className="login-activity-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>ユーザー</th>
                <th>件名</th>
                <th>最終更新</th>
                <th>件数</th>
                <th>最新評価</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {conversations.length === 0 ? (
                <tr>
                  <td colSpan={7} className="login-activity-empty-cell">お問い合わせ履歴はまだありません。</td>
                </tr>
              ) : (
                conversations.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>{item.username || '-'}</td>
                    <td>{item.subjectLabel}</td>
                    <td>{formatDateTime(item.updatedAt)}</td>
                    <td>{item.messageCount || 0}</td>
                    <td>
                      {item.latestAssistantFeedback === 'up'
                        ? '👍 分かりやすい'
                        : item.latestAssistantFeedback === 'unclear'
                          ? 'うーん'
                          : '-'}
                    </td>
                    <td>
                      <button onClick={() => loadConversationDetail(item.id)} style={{ padding: '0.275rem 0.425rem' }}>
                        詳細
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="login-activity-panel">
        <div className="login-activity-panel-header">
          <div>
            <h3>お問い合わせ詳細</h3>
            <p>{selectedConversation ? `件名: ${selectedConversation.subjectLabel}` : '一覧からお問い合わせを選択してください。'}</p>
          </div>
        </div>

        {isLoadingDetail ? (
          <p>読み込み中...</p>
        ) : !selectedConversation ? (
          <p>対象未選択</p>
        ) : (
          <div style={{ display: 'grid', gap: '1rem' }}>
            <div style={{ display: 'grid', gap: '0.25rem' }}>
              <div>ユーザー: {selectedConversation.username || '-'}</div>
              <div>件名: {selectedConversation.subjectLabel}</div>
              <div>受付日時: {formatDateTime(selectedConversation.createdAt, usesLegacyJstTimestamps)}</div>
              <div>最終更新: {formatDateTime(selectedConversation.updatedAt)}</div>
              <div>最終メッセージ: {formatDateTime(selectedConversation.lastMessageAt)}</div>
              {selectedConversation.subjectFreeText && <div>件名補足: {selectedConversation.subjectFreeText}</div>}
            </div>
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              {messages.map((message) => (
                <div
                  key={message.id}
                  style={{
                    display: 'grid',
                    justifyItems: message.role === 'assistant' ? 'end' : 'start',
                  }}
                >
                  <div
                    style={{
                      width: '80%',
                      border: '1px solid #e5e7eb',
                      borderRadius: 8,
                      padding: '0.75rem',
                      background: message.role === 'assistant' ? '#f8fafc' : '#ffffff',
                    }}
                  >
                    <div style={{ fontWeight: 700, marginBottom: 6 }}>
                      {message.role === 'assistant' ? 'AI回答' : (message.username || 'ユーザー')}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>{message.messageText}</div>
                    <div style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>
                      {formatDateTime(message.createdAt, usesLegacyJstTimestamps)}
                    </div>
                    {message.role === 'assistant' && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#374151' }}>
                        <div>FAQ見本: {message.faqDocumentName || '-'}</div>
                        <div>モデル: {message.modelName || '-'}</div>
                        <div>
                          評価:
                          {' '}
                          {message.feedbackValue === 'up'
                            ? '👍 分かりやすい'
                            : message.feedbackValue === 'unclear'
                              ? 'うーん（よくわからない）'
                              : '未評価'}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default ContactHistoryDashboard;
